// MCP 连接指南浮层 / MCP connection guide modal.
// 只渲染本地连接信息，不发起任何网络请求。
// Renders local connection details only; never performs network requests.

/** MCP 指南打开参数 / Options for opening the MCP guide. */
export interface McpGuideModalOptions {
  token?: string;
  apiBaseUrl?: string;
}

let modalEvents: AbortController | null = null;

/**
 * 打开 MCP 连接指南浮层 / Open the MCP connection guide modal.
 *
 * URL 和 token 都在打开时同步计算，避免弹窗出现后再等待服务端返回。
 * URL and token are computed synchronously so the modal appears immediately.
 */
export function openMcpGuideModal(opts: McpGuideModalOptions = {}): void {
  const root = _modalRoot();
  modalEvents?.abort();
  modalEvents = new AbortController();
  const { signal } = modalEvents;

  const token = opts.token ?? "";
  const mcpUrl = `${_apiBaseUrl(opts.apiBaseUrl)}/mcp/`;
  const maskedToken = _maskToken(token);
  const skillUrl = `${location.origin}/skill.md`;
  const authHeader = `Authorization: Bearer ${token}`;
  const maskedAuthHeader = `Authorization: Bearer ${maskedToken}`;
  const urlTokenUrl = _withQueryParam(mcpUrl, "rippleApiKey", token);
  const maskedUrlTokenUrl = _withQueryParam(mcpUrl, "rippleApiKey", maskedToken);

  root.hidden = false;
  root.innerHTML = `
    <div class="overlay" aria-hidden="true"></div>
    <section class="modal mcp-guide-panel" role="dialog" aria-modal="true" aria-labelledby="mcp-guide-title">
      <header class="mcp-guide-head">
        <div>
          <p class="mcp-guide-kicker">Agent setup</p>
          <h3 id="mcp-guide-title">Connect your agent via MCP</h3>
        </div>
        <button class="mcp-guide-close" type="button" aria-label="Close MCP guide">×</button>
      </header>

      <div class="mcp-guide-body">
        <section class="mcp-guide-section" aria-labelledby="mcp-guide-server-title">
          <h4 id="mcp-guide-server-title">MCP Server</h4>
          <p class="mcp-guide-mode-title">Header token mode</p>
          ${_valueRow("URL", "mcp-url", mcpUrl, "mcp-url")}
          ${_tokenRow(maskedToken)}
          ${_valueRow("Header", "header", maskedAuthHeader, "header")}
          <p class="mcp-guide-mode-title">URL token mode</p>
          ${_valueRow("URL", "url-token-url", maskedUrlTokenUrl, "url-token-url")}
          <p class="mcp-guide-note">Use this mode only for clients that cannot send custom headers.</p>
        </section>

        <section class="mcp-guide-section" aria-labelledby="mcp-guide-skill-title">
          <h4 id="mcp-guide-skill-title">Skill</h4>
          ${_skillRow(skillUrl)}
          <p class="mcp-guide-note">No auth required; safe to share with anyone.</p>
        </section>

        <p class="mcp-guide-warning">
          Your token grants full access to your Ripple account. Never paste it into third-party chats,
          issue trackers, or public logs.
        </p>
      </div>
    </section>
  `;

  const values: Record<string, string> = {
    "mcp-url": mcpUrl,
    token,
    header: authHeader,
    "url-token-url": urlTokenUrl,
    skill: skillUrl,
  };

  root.querySelector<HTMLButtonElement>(".mcp-guide-close")
    ?.addEventListener("click", closeMcpGuideModal, { signal });

  root.querySelector<HTMLButtonElement>(".mcp-token-toggle")
    ?.addEventListener("click", (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const value = root.querySelector<HTMLElement>("[data-field='token']");
      if (!value) return;
      const show = button.getAttribute("aria-pressed") !== "true";
      button.setAttribute("aria-pressed", String(show));
      button.textContent = show ? "Hide" : "Show";
      value.textContent = show ? token : _maskToken(token);
    }, { signal });

  root.querySelectorAll<HTMLButtonElement>("button.mcp-copy-btn").forEach((button) => {
    const key = button.dataset.copy;
    if (!key || !(key in values)) return;
    button.addEventListener("click", () => {
      _copyWithFeedback(button, values[key] ?? "");
    }, { signal });
  });
}

// 关闭并清理事件监听 / Close the modal and clear event listeners.
function closeMcpGuideModal(): void {
  const root = document.getElementById("mcp-guide-modal");
  modalEvents?.abort();
  modalEvents = null;
  if (!root) return;
  root.hidden = true;
  root.innerHTML = "";
}

// 复制完整值，按钮立即进入 loading，成功时先展示 Copied，再恢复原文案。
// Copy the full value, enter loading immediately, show Copied on success, then restore.
function _copyWithFeedback(button: HTMLButtonElement, value: string): void {
  if (button.disabled) return;
  const originalHtml = button.innerHTML;
  const originalDisabled = button.disabled;
  let copied = false;
  button.disabled = true;
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");
  button.innerHTML = `<span class="button-spinner" aria-hidden="true"></span><span>Copying</span>`;

  const clipboard = navigator.clipboard;
  if (clipboard?.writeText) {
    void clipboard.writeText(value)
      .then(() => { copied = true; })
      .catch(() => {
        // 复制失败不打断用户流程；按钮按固定节奏恢复。
        // Clipboard failures should not break the UI; restore on the fixed cadence.
      });
  }

  window.setTimeout(() => {
    button.disabled = originalDisabled;
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    if (!copied) {
      button.innerHTML = originalHtml;
      return;
    }
    button.innerHTML = `<span>Copied ✓</span>`;
    window.setTimeout(() => {
      button.innerHTML = originalHtml;
      button.disabled = originalDisabled;
    }, 800);
  }, 200);
}

// 计算 MCP 后端直连 base；Vite 只代理 /api，不代理 /mcp。
// Compute the direct backend base; Vite proxies /api only, never /mcp.
function _apiBaseUrl(explicitBase: string | undefined): string {
  const envBase = (import.meta.env.VITE_API_BASE_URL ?? "").trim().replace(/\/+$/, "");
  if (envBase) return envBase;
  const explicit = (explicitBase ?? "").trim().replace(/\/+$/, "");
  if (explicit) return explicit;
  return `http://${location.hostname}:8000`;
}

function _valueRow(label: string, field: string, value: string, copyKey: string): string {
  return `
    <div class="mcp-guide-row">
      <span class="mcp-guide-label">${_escape(label)}</span>
      <code class="mcp-guide-value" data-field="${_escape(field)}">${_escape(value)}</code>
      <button class="mcp-copy-btn" type="button" data-copy="${_escape(copyKey)}">
        <span>Copy</span>
      </button>
    </div>
  `;
}

function _skillRow(value: string): string {
  return `
    <div class="mcp-guide-row mcp-skill-row">
      <span class="mcp-guide-label">URL</span>
      <code class="mcp-guide-value" data-field="skill-url">${_escape(value)}</code>
      <a class="mcp-copy-btn mcp-skill-download" href="/skill.md" download="skill.md">
        <span>Download</span>
      </a>
      <button class="mcp-copy-btn" type="button" data-copy="skill">
        <span>Copy</span>
      </button>
    </div>
  `;
}

function _tokenRow(maskedToken: string): string {
  return `
    <div class="mcp-guide-row mcp-token-row">
      <span class="mcp-guide-label">Token</span>
      <code class="mcp-guide-value" data-field="token">${_escape(maskedToken)}</code>
      <button class="mcp-token-toggle" type="button" aria-pressed="false">Show</button>
      <button class="mcp-copy-btn" type="button" data-copy="token">
        <span>Copy</span>
      </button>
    </div>
  `;
}

function _maskToken(token: string): string {
  if (token.length <= 8) return token ? `${token.slice(0, 4)}****` : "****";
  return `${token.slice(0, 4)}****${token.slice(-4)}`;
}

// 拼接 URL token 参数，复制值保留完整 token / Append URL-token query while preserving the full copy value.
function _withQueryParam(baseUrl: string, key: string, value: string): string {
  return `${baseUrl}?${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

function _modalRoot(): HTMLElement {
  const root = document.getElementById("mcp-guide-modal");
  if (!root) throw new Error("#mcp-guide-modal mount missing");
  return root;
}

function _escape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
